/**
 * The engine's built-in assets: basic shapes a Mesh Renderer can show without
 * downloading anything, like Unreal's `/Engine/BasicShapes` and Unity's
 * built-in Cube. They sit beside the content catalog's assets in pickers and
 * are referenced by path the same way, under `engine/`, which no content
 * path uses (content lives under `assets/`).
 *
 * This names the shapes; how each one is drawn is the renderer's business.
 */

export type EngineShape = "box" | "sphere" | "cylinder" | "plane" | "platform";

export interface EngineMesh {
  /** What an assetRef holds, e.g. `engine/shapes/Box`. */
  readonly path: string;
  /** The picker's label. */
  readonly name: string;
  readonly shape: EngineShape;
}

/** The asset kind engine shapes have, for assetRef props' `kinds`. */
export const SHAPE_ASSET_KIND = "shape";

const ENGINE_ROOT = "engine/";
const SHAPES_FOLDER = `${ENGINE_ROOT}shapes/`;

const shape = (name: string, key: EngineShape): EngineMesh => ({
  path: `${SHAPES_FOLDER}${name}`,
  name,
  shape: key,
});

export const ENGINE_MESHES: readonly EngineMesh[] = [
  shape("Box", "box"),
  shape("Sphere", "sphere"),
  shape("Cylinder", "cylinder"),
  shape("Plane", "plane"),
  shape("Platform", "platform"),
];

/** The shape an engine path names, or null for content (or an unknown engine path). */
export function engineShapeOf(path: string): EngineShape | null {
  return ENGINE_MESHES.find((mesh) => mesh.path === path)?.shape ?? null;
}

/** The engine path of a shape (format upgrades and presets write shapes this way). */
export function engineMeshPath(key: EngineShape): string {
  const mesh = ENGINE_MESHES.find((candidate) => candidate.shape === key);
  if (!mesh) throw new Error(`No engine mesh for '${key}'`);
  return mesh.path;
}

export function isEngineAsset(path: string): boolean {
  return path.startsWith(ENGINE_ROOT);
}
